import { Component, signal, computed, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { HttpCancelacionSitioRepository } from 'src/app/infrastructure/adapters/cancelacion-sitio/http-cancelacion-sitio.repository';
import { HttpOficinaRepository } from 'src/app/infrastructure/adapters/oficina/http-oficina.repository';
import { sanitizeText } from 'src/app/common/input-utils';
import { sanitizarTexto } from 'src/app/common/sanitizer.util';
import {
  CancelacionSitioEncabezado,
  CancelacionSitioFiltro,
  CancelacionSitioRequest
} from 'src/app/domain/model/cancelacion-sitio/cancelacion-sitio';
import { Oficina } from 'src/app/domain/model/oficina/oficina';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';
import { cancelacionSitioListadoPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';

/**
 * Componente principal de Cancelaciones en Sitio.
 *
 * Migrado desde: CancelacionSitioNoEnviadasViewImpl.java (GWT)
 * Funcionalidad: Listado de encabezados de cancelación con filtros
 *
 * Filtros del legacy:
 * - tbEncabezado: Nombre/descripción del encabezado
 * - lbOficina: Dropdown de oficinas
 * - lbAnio: Dropdown de años
 * - lbMes: Dropdown de meses (1-12)
 *
 * @author Sura - Migración GWT a Angular
 * @version 1.0
 * @since Mayo 2026
 */
@Component({
  selector: 'app-cancelacion-sitio',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    ToastModule,
    TooltipModule,
    ProgressSpinnerModule,
    ConfirmDialogModule,
    DialogModule,
    DatePickerModule
  ],
  providers: [MessageService, ConfirmationService],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  template: `
    <!-- Overlay de procesando eliminación -->
    @if (eliminando()) {
      <div
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      >
        <div
          class="bg-white rounded-lg p-8 flex flex-col items-center gap-4 shadow-xl"
        >
          <p-progressSpinner strokeWidth="4" />
          <span class="text-gray-700 font-medium"
            >Eliminando encabezado...</span
          >
        </div>
      </div>
    }

    <!-- Overlay de enviando a nómina -->
    @if (enviando()) {
      <div
        class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      >
        <div
          class="bg-white rounded-lg p-8 flex flex-col items-center gap-4 shadow-xl"
        >
          <p-progressSpinner strokeWidth="4" />
          <span class="text-gray-700 font-medium">Enviando a nómina...</span>
        </div>
      </div>
    }

    <div
      class="p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto flex flex-col gap-4 overflow-x-hidden"
    >
      <!-- Header -->
      <div class="mb-6">
        <h1 class="text-2xl font-bold text-gray-800">Cancelaciones en Sitio</h1>
        <p class="text-gray-600">
          Gestión de encabezados de cancelaciones en sitio para pago de
          transporte
        </p>
      </div>

      <!-- Filtros de Búsqueda -->
      <p-card header="Filtros de Búsqueda" class="mb-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <!-- Nombre Encabezado -->
          <div class="flex flex-col gap-2">
            <label for="nombreEncabezado" class="font-medium text-gray-700">
              Nombre Encabezado
            </label>
            <input
              pInputText
              id="nombreEncabezado"
              [ngModel]="filtros().nombreEncabezado"
              (change)="onNombreEncabezadoInput($event)"
              placeholder="Buscar por nombre..."
              maxlength="30"
              class="w-full"
            />
          </div>

          <!-- Oficina -->
          <div class="flex flex-col gap-2">
            <label for="oficina" class="font-medium text-gray-700">
              Oficina
            </label>
            <p-dropdown
              id="oficina"
              [filter]="true"
              filterBy="dsoficina"
              filterPlaceholder="Buscar oficina..."
              [resetFilterOnHide]="true"
              emptyFilterMessage="Sin coincidencias"
              [options]="oficinas()"
              [(ngModel)]="filtros().nmconsecOficina"
              (ngModelChange)="actualizarFiltro('nmconsecOficina', $event)"
              optionLabel="dsoficina"
              optionValue="nmconsecutivo"
              placeholder="Todas las oficinas"
              [showClear]="true"
              styleClass="w-full"
            >
            </p-dropdown>
          </div>

          <!-- Año -->
          <div class="flex flex-col gap-2">
            <label for="anio" class="font-medium text-gray-700">Año</label>
            <p-dropdown
              id="anio"
              [options]="aniosDisponibles()"
              [(ngModel)]="filtros().anio"
              (ngModelChange)="actualizarFiltro('anio', $event)"
              placeholder="Todos los años"
              [showClear]="true"
              styleClass="w-full"
            >
            </p-dropdown>
          </div>

          <!-- Mes -->
          <div class="flex flex-col gap-2">
            <label for="mes" class="font-medium text-gray-700">Mes</label>
            <p-dropdown
              id="mes"
              [options]="meses"
              [(ngModel)]="filtros().mes"
              (ngModelChange)="actualizarFiltro('mes', $event)"
              optionLabel="label"
              optionValue="value"
              placeholder="Todos los meses"
              [showClear]="true"
              styleClass="w-full"
            >
            </p-dropdown>
          </div>
        </div>

        <!-- Botones de Acción -->
        <div
          class="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-end sm:justify-start gap-2 sm:gap-3 mt-4"
        >
          <button
            type="button"
            pButton
            label="Limpiar"
            icon="pi pi-eraser"
            severity="secondary"
            class="p-button-outlined w-full sm:w-auto min-h-[44px]"
            (click)="limpiarFiltros()"
          ></button>
          <button
            type="button"
            pButton
            icon="pi pi-search"
            label="Buscar"
            class="p-button-primary w-full sm:w-auto min-h-[44px]"
            (click)="buscar()"
            [loading]="loading()"
          ></button>
          <button
            type="button"
            pButton
            icon="pi pi-plus"
            label="Nuevo Encabezado"
            (click)="nuevoEncabezado()"
            severity="success"
            class="p-button-outlined w-full sm:w-auto min-h-[44px]"
          ></button>
        </div>
      </p-card>

      <!-- Tabla de Resultados -->
      <p-card header="Encabezados de Cancelación" class="mb-6">
        @if (loading()) {
          <div class="flex justify-center items-center py-12">
            <p-progressSpinner strokeWidth="4" />
          </div>
        } @else if (encabezados().length === 0) {
          <div class="text-center py-12 text-gray-500">
            <div class="text-4xl mb-4"></div>
            <p>
              No se encontraron encabezados de cancelaciones en sitio con los
              filtros aplicados
            </p>
          </div>
        } @else {
          <div class="md:hidden space-y-3 p-3">
            @for (encabezado of encabezados(); track encabezado.nmconsecutivo) {
              <div
                class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
              >
                <div class="mb-3 min-w-0">
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    Nombre
                  </div>
                  <div
                    class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                  >
                    {{ encabezado.dsencabezado }}
                  </div>
                </div>
                <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Fecha inicio:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      encabezado.feiniCancelacion | date: 'dd/MM/yyyy'
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Fecha fin:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      encabezado.fefinCancelacion | date: 'dd/MM/yyyy'
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Oficina:</span
                    >
                    <span class="text-gray-800 dark:text-gray-200">{{
                      encabezado.dsoficina
                    }}</span>
                  </div>
                  <div>
                    <span class="text-gray-500 dark:text-gray-400"
                      >Estado:</span
                    >
                    <span
                      class="ml-1"
                      [class]="getEstadoClass(encabezado.cdestado)"
                      >{{ encabezado.dsestado || encabezado.cdestado }}</span
                    >
                  </div>
                </div>
                <div class="grid grid-cols-3 gap-2">
                  <button
                    class="min-w-[44px] min-h-[44px] p-button-outlined p-button-info p-button-sm"
                    pButton
                    icon="pi pi-list"
                    pTooltip="Ver Detalles"
                    (click)="verDetalles(encabezado)"
                  ></button>
                  @if (
                    encabezado.cdestado === 'PEN' && !encabezado.feenvioNomina
                  ) {
                    <button
                      class="min-w-[44px] min-h-[44px] p-button-outlined p-button-success p-button-sm"
                      pButton
                      icon="pi pi-send"
                      pTooltip="Enviar a Nómina"
                      (click)="enviarANomina(encabezado)"
                    ></button>
                    <button
                      class="min-w-[44px] min-h-[44px] p-button-outlined p-button-danger p-button-sm"
                      pButton
                      icon="pi pi-trash"
                      pTooltip="Eliminar"
                      (click)="eliminar(encabezado)"
                    ></button>
                  } @else {
                    <span></span>
                    <span></span>
                  }
                </div>
              </div>
            }
          </div>

          <div class="hidden md:block">
            <div
              role="region"
              [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
              class="sr-only"
            ></div>
            <p-table
              [resizableColumns]="true"
              columnResizeMode="expand"
              [paginator]="false"
              [rows]="encabezados().length"
              [value]="encabezados()"
              [scrollable]="true"
              [responsiveLayout]="'scroll'"
              [tableStyle]="{
                'min-width': tableConfig().responsive.minWidth || '72rem'
              }"
              [scrollHeight]="tableConfig().responsive.scrollHeight || '420px'"
              [styleClass]="tableStyleClass()"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th pResizableColumn class="text-center" style="width: 80px">
                    <span class="grid-header-text" title="Detalles"
                      >Detalles</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Nombre">Nombre</span>
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Fecha Inicio"
                      >Fecha Inicio</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Fecha Fin"
                      >Fecha Fin</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Oficina"
                      >Oficina</span
                    >
                  </th>
                  <th pResizableColumn>
                    <span class="grid-header-text" title="Estado">Estado</span>
                  </th>
                  <th pResizableColumn class="text-center" style="width: 150px">
                    <span class="grid-header-text" title="Acciones"
                      >Acciones</span
                    >
                  </th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-encabezado>
                <tr>
                  <td class="text-center">
                    <button
                      pButton
                      icon="pi pi-list"
                      class="p-button-rounded p-button-text p-button-sm p-button-info min-w-[44px] min-h-[44px]"
                      pTooltip="Ver Detalles"
                      (click)="verDetalles(encabezado)"
                    ></button>
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="textoTabla(encabezado.dsencabezado)"
                      >{{ textoTabla(encabezado.dsencabezado) }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="
                        textoTabla(
                          encabezado.feiniCancelacion | date: 'dd/MM/yyyy'
                        )
                      "
                      >{{
                        textoTabla(
                          encabezado.feiniCancelacion | date: 'dd/MM/yyyy'
                        )
                      }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="
                        textoTabla(
                          encabezado.fefinCancelacion | date: 'dd/MM/yyyy'
                        )
                      "
                      >{{
                        textoTabla(
                          encabezado.fefinCancelacion | date: 'dd/MM/yyyy'
                        )
                      }}</span
                    >
                  </td>
                  <td>
                    <span
                      class="grid-cell-text"
                      [attr.title]="textoTabla(encabezado.dsoficina)"
                      >{{ textoTabla(encabezado.dsoficina) }}</span
                    >
                  </td>
                  <td>
                    <span [class]="getEstadoClass(encabezado.cdestado)">
                      {{ encabezado.dsestado || encabezado.cdestado }}
                    </span>
                  </td>
                  <td class="text-center">
                    <div class="flex gap-2 justify-center">
                      @if (
                        encabezado.cdestado === 'PEN' &&
                        !encabezado.feenvioNomina
                      ) {
                        <button
                          pButton
                          icon="pi pi-send"
                          class="p-button-rounded p-button-text p-button-sm p-button-success min-w-[44px] min-h-[44px]"
                          pTooltip="Enviar a Nómina"
                          (click)="enviarANomina(encabezado)"
                        ></button>
                        <button
                          pButton
                          icon="pi pi-trash"
                          class="p-button-rounded p-button-text p-button-sm p-button-danger min-w-[44px] min-h-[44px]"
                          pTooltip="Eliminar"
                          (click)="eliminar(encabezado)"
                        ></button>
                      }
                    </div>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </div>
        }
      </p-card>

      <p-toast></p-toast>
      <p-confirmDialog></p-confirmDialog>

      <!-- Dialog Crear Encabezado -->
      <p-dialog
        header="Nuevo Encabezado de Cancelaciones en Sitio"
        [(visible)]="showCrearDialog"
        [modal]="true"
        [style]="{ width: '92vw', maxWidth: '36rem' }"
        [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
        [breakpoints]="{ '768px': '94vw', '640px': '96vw' }"
        [closable]="!creando()"
      >
        <div class="grid gap-4">
          <div class="flex flex-col gap-2">
            <label for="nuevoNombre" class="font-medium text-gray-700"
              >Nombre Encabezado *</label
            >
            <input
              pInputText
              id="nuevoNombre"
              [ngModel]="nuevoEncabezadoForm.dsencabezado"
              (change)="onNuevoNombreEncabezadoInput($event)"
              maxlength="30"
              placeholder="Máximo 30 caracteres"
              class="w-full"
            />
          </div>
          <div class="flex flex-col gap-2">
            <label for="nuevaOficina" class="font-medium text-gray-700"
              >Oficina *</label
            >
            <p-dropdown
              id="nuevaOficina"
              [filter]="true"
              filterBy="dsoficina"
              filterPlaceholder="Buscar oficina..."
              [resetFilterOnHide]="true"
              emptyFilterMessage="Sin coincidencias"
              [options]="oficinas()"
              [(ngModel)]="nuevoEncabezadoForm.nmconsecOficina"
              optionLabel="dsoficina"
              optionValue="nmconsecutivo"
              placeholder="Seleccione oficina"
              styleClass="w-full"
              appendTo="body"
              [autoZIndex]="true"
              [baseZIndex]="10000"
            >
            </p-dropdown>
          </div>
          <div class="flex flex-col gap-2">
            <label for="nuevaFechaIni" class="font-medium text-gray-700"
              >Fecha Inicial *</label
            >
            <p-datepicker
              id="nuevaFechaIni"
              [(ngModel)]="nuevoEncabezadoForm.feiniCancelacion"
              dateFormat="dd/mm/yy"
              [showIcon]="true"
              [appendTo]="'body'"
              styleClass="w-full"
            >
            </p-datepicker>
          </div>
          <div class="flex flex-col gap-2">
            <label for="nuevaFechaFin" class="font-medium text-gray-700"
              >Fecha Final *</label
            >
            <p-datepicker
              id="nuevaFechaFin"
              [(ngModel)]="nuevoEncabezadoForm.fefinCancelacion"
              dateFormat="dd/mm/yy"
              [showIcon]="true"
              [appendTo]="'body'"
              styleClass="w-full"
            >
            </p-datepicker>
          </div>
        </div>
        <ng-template pTemplate="footer">
          <div
            class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
          >
            <button
              type="button"
              pButton
              icon="pi pi-times"
              class="p-button-outlined p-button-sm min-h-[44px]"
              label="Cancelar"
              severity="secondary"
              (click)="cerrarDialogCrear()"
              [disabled]="creando()"
            ></button>
            <button
              type="button"
              pButton
              icon="pi pi-check"
              class="p-button-success p-button-sm min-h-[44px]"
              label="Guardar"
              severity="primary"
              (click)="guardarNuevoEncabezado()"
              [loading]="creando()"
            ></button>
          </div>
        </ng-template>
      </p-dialog>
    </div>
  `
})
export class CancelacionSitioComponent implements OnInit {
  readonly sanitizeText = sanitizeText;

  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly repository = inject(HttpCancelacionSitioRepository);
  private readonly oficinaRepository = inject(HttpOficinaRepository);
  private readonly usuarioState = inject(UsuarioStateService);

  // Estado reactivo
  readonly loading = signal(false);
  readonly encabezados = signal<CancelacionSitioEncabezado[]>([]);
  readonly totalRecords = signal(0);
  readonly pageSize = signal(10);

  readonly tableConfig = computed(() =>
    cancelacionSitioListadoPreset.createConfig({
      data: this.encabezados(),
      style: {
        tableClassName: 'tabla-cambio-orientacion',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage:
          'No se encontraron encabezados de cancelaciones en sitio con los filtros aplicados'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '72rem',
        scrollHeight: '420px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de encabezados de cancelacion en sitio',
        announceSelection: false,
        announcePageChanges: false,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = computed(() => {
    const config = this.tableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  // Filtros - Inicializados con mes y año actual según legacy (HU #807572)
  readonly filtros = signal<CancelacionSitioFiltro>({
    nombreEncabezado: '',
    nmconsecOficina: null,
    anio: new Date().getFullYear(),
    mes: new Date().getMonth() + 1
  });

  // Catálogos
  readonly oficinas = signal<Oficina[]>([]);
  readonly aniosDisponibles = computed(() => {
    const anioActual = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => anioActual - i);
  });

  readonly meses = [
    { label: 'Enero', value: 1 },
    { label: 'Febrero', value: 2 },
    { label: 'Marzo', value: 3 },
    { label: 'Abril', value: 4 },
    { label: 'Mayo', value: 5 },
    { label: 'Junio', value: 6 },
    { label: 'Julio', value: 7 },
    { label: 'Agosto', value: 8 },
    { label: 'Septiembre', value: 9 },
    { label: 'Octubre', value: 10 },
    { label: 'Noviembre', value: 11 },
    { label: 'Diciembre', value: 12 }
  ];

  // Dialog de creación
  showCrearDialog = false;
  readonly creando = signal(false);
  readonly eliminando = signal(false);
  readonly enviando = signal(false);
  nuevoEncabezadoForm = {
    dsencabezado: '',
    nmconsecOficina: null as number | null,
    feiniCancelacion: null as Date | null,
    fefinCancelacion: null as Date | null
  };

  ngOnInit(): void {
    // Cargar usuario en sesión (hasta que haya SSO)
    void this.cargarDatosUsuario();
    this.cargarOficinas();
    this.buscar();
  }

  /**
   * Carga los datos del usuario actual en sesión.
   * Usa el servicio de estado centralizado que persiste en sessionStorage.
   *
   * @legacy PrestacionServiciosCGRHandler.sessionData()
   */
  private async cargarDatosUsuario(): Promise<void> {
    try {
      await this.usuarioState.cargar();
    } catch (error) {
      console.error(
        '[CancelacionSitio] Error cargando datos de usuario:',
        error
      );
    }
  }

  actualizarFiltro<K extends keyof CancelacionSitioFiltro>(
    campo: K,
    valor: CancelacionSitioFiltro[K]
  ): void {
    if (campo === 'nombreEncabezado' && typeof valor === 'string') {
      valor = this.sanitizeText(valor) as CancelacionSitioFiltro[K];
    }
    this.filtros.update((f) => ({ ...f, [campo]: valor }));
  }

  onNombreEncabezadoInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitizedValue = this.sanitizeText(input.value);
    input.value = sanitizedValue;
    this.actualizarFiltro('nombreEncabezado', sanitizedValue);
  }

  onNuevoNombreEncabezadoInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitizedValue = this.sanitizeText(input.value);
    input.value = sanitizedValue;
    this.nuevoEncabezadoForm.dsencabezado = sanitizedValue;
  }

  async cargarOficinas(): Promise<void> {
    try {
      const oficinas = await this.oficinaRepository.obtenerOficinas();
      this.oficinas.set(oficinas);
    } catch (error) {
      console.error('Error cargando oficinas:', error);
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'No se pudieron cargar las oficinas'
      });
    }
  }

  async buscar(): Promise<void> {
    this.loading.set(true);
    try {
      // Sanitizar filtro de nombre contra XSS/SQLi
      const filtrosActuales = this.filtros();
      // seguridad-girop §3: sanitización de formulario (containsDangerousContent es solo para carga masiva)
      const nombreResult = sanitizarTexto(
        filtrosActuales.nombreEncabezado,
        'Nombre Encabezado',
        { maxLength: 30 }
      );
      if (!nombreResult.valido) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validación',
          detail: nombreResult.error ?? 'Nombre inválido'
        });
        this.loading.set(false);
        return;
      }
      const nombreSanitizado = nombreResult.texto ?? '';

      const filtrosSanitizados: CancelacionSitioFiltro = {
        ...filtrosActuales,
        nombreEncabezado: nombreSanitizado
      };

      // NOTA UX: con paginación Keyset/Cursor ya no es posible saltar a un
      // offset arbitrario (this.first() se conserva sólo para compatibilidad
      // de la señal, pero ya no se traduce a un cursor real); toda búsqueda
      // (incluida onPageChange) reinicia a la primera página del backend.
      const response = await this.repository.listar(
        filtrosSanitizados,
        null,
        this.pageSize(),
        'NEXT'
      );

      this.encabezados.set(response.content);
      this.totalRecords.set(response.totalElements);

      this.messageService.add({
        severity: 'success',
        summary: 'Búsqueda completada',
        detail: `Se encontraron ${response.totalElements} encabezados`
      });
    } catch (error) {
      console.error('Error en búsqueda:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          error instanceof Error
            ? error.message
            : 'No se pudo realizar la búsqueda'
      });
    } finally {
      this.loading.set(false);
    }
  }

  limpiarFiltros(): void {
    // Al limpiar, restablecer a mes y año actual (igual que el legacy)
    this.filtros.set({
      nombreEncabezado: '',
      nmconsecOficina: null,
      anio: new Date().getFullYear(),
      mes: new Date().getMonth() + 1
    });
    this.buscar();
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  getEstadoClass(estado: string): string {
    const clases: Record<string, string> = {
      PEN: 'px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800',
      ENV: 'px-2 py-1 text-xs rounded-full bg-green-100 text-green-800',
      ANU: 'px-2 py-1 text-xs rounded-full bg-red-100 text-red-800'
    };
    return (
      clases[estado] ||
      'px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800'
    );
  }

  /**
   * Abre el dialog para crear un nuevo encabezado.
   * Migrado desde: NuevoEncabezadoCancelacionSitioViewImpl.java
   */
  nuevoEncabezado(): void {
    this.nuevoEncabezadoForm = {
      dsencabezado: '',
      nmconsecOficina: null,
      feiniCancelacion: null,
      fefinCancelacion: null
    };
    this.showCrearDialog = true;
  }

  /**
   * Cierra el dialog de creación sin guardar.
   */
  cerrarDialogCrear(): void {
    this.showCrearDialog = false;
  }

  /**
   * Guarda el nuevo encabezado.
   * Valida campos obligatorios y rango de fechas antes de enviar.
   */
  async guardarNuevoEncabezado(): Promise<void> {
    const form = this.nuevoEncabezadoForm;

    // Validaciones de campos obligatorios
    if (!form.dsencabezado?.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'El nombre es obligatorio'
      });
      return;
    }

    // Sanitizar nombre contra XSS/SQLi (defensa en profundidad)
    const nombreResult = sanitizarTexto(
      form.dsencabezado,
      'Nombre Encabezado',
      { maxLength: 30 }
    );
    if (!nombreResult.valido) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: nombreResult.error ?? 'Nombre inválido'
      });
      return;
    }
    const nombreSanitizado = nombreResult.texto ?? '';

    if (!form.nmconsecOficina) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'La oficina es obligatoria'
      });
      return;
    }
    if (!form.feiniCancelacion || !form.fefinCancelacion) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Las fechas son obligatorias'
      });
      return;
    }
    if (form.fefinCancelacion < form.feiniCancelacion) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Fecha final debe ser mayor o igual a fecha inicial'
      });
      return;
    }

    this.creando.set(true);
    try {
      const request: CancelacionSitioRequest = {
        dsencabezado: nombreSanitizado,
        nmconsecOficina: form.nmconsecOficina,
        feiniCancelacion: this.formatDateToISO(form.feiniCancelacion),
        fefinCancelacion: this.formatDateToISO(form.fefinCancelacion)
      };

      await this.repository.crear(request);

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Encabezado creado correctamente'
      });
      this.showCrearDialog = false;
      this.buscar(); // Recargar lista
    } catch (error: unknown) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : 'No se pudo crear el encabezado';
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: errorMsg
      });
    } finally {
      this.creando.set(false);
    }
  }

  /**
   * Convierte una fecha a formato ISO (YYYY-MM-DD).
   */
  private formatDateToISO(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Navega a la vista de detalles del encabezado.
   * Migrado desde: CancelacionSitioNoEnviadasViewImpl.java (addLinkColumn "Detalles")
   */
  verDetalles(encabezado: CancelacionSitioEncabezado): void {
    this.router.navigate(['/cancelaciones-sitio', encabezado.nmconsecutivo]);
  }

  /**
   * Acción "Detalles" del legacy - Navega a Modificar Encabezado.
   * Migrado desde: CancelacionSitioNoEnviadasViewImpl.java (addLinkColumn "Detalles")
   */
  modificarEncabezado(encabezado: CancelacionSitioEncabezado): void {
    // TODO: Navegar a la pantalla de modificar encabezado cuando esté implementada
    this.messageService.add({
      severity: 'info',
      summary: 'Modificar Encabezado',
      detail: `Navegando a modificar encabezado #${encabezado.nmconsecutivo}: ${encabezado.dsencabezado}`
    });
  }

  /**
   * Elimina (anula) un encabezado de cancelación.
   * Migrado desde: CancelacionSitioNoEnviadasViewImpl.java (eliminarColumn)
   * Solo disponible si cdestado = 'PEN' y no ha sido enviado a nómina.
   */
  eliminar(encabezado: CancelacionSitioEncabezado): void {
    this.confirmationService.confirm({
      message: `¿Está seguro de eliminar el encabezado "${encabezado.dsencabezado}"?`,
      header: 'Confirmar Eliminación',
      icon: 'pi pi-trash',
      acceptLabel: 'Sí, Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-outlined p-button-danger p-button-sm',
      rejectButtonStyleClass:
        'p-button-outlined p-button-secondary p-button-sm',
      accept: async () => {
        this.eliminando.set(true);
        try {
          await this.repository.anular(encabezado.nmconsecutivo!);
          this.messageService.add({
            severity: 'success',
            summary: 'Eliminado',
            detail: 'Encabezado eliminado correctamente'
          });
          this.buscar(); // Recargar lista
        } catch (error) {
          console.error('Error eliminando encabezado:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail:
              error instanceof Error
                ? error.message
                : 'No se pudo eliminar el encabezado'
          });
        } finally {
          this.eliminando.set(false);
        }
      }
    });
  }

  enviarANomina(encabezado: CancelacionSitioEncabezado): void {
    this.confirmationService.confirm({
      message: `¿Está seguro de enviar a nómina el encabezado "${encabezado.dsencabezado}"?`,
      header: 'Confirmar Envío a Nómina',
      icon: 'pi pi-send',
      acceptLabel: 'Sí, Enviar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-outlined p-button-success p-button-sm',
      rejectButtonStyleClass:
        'p-button-outlined p-button-secondary p-button-sm',
      accept: async () => {
        this.enviando.set(true);
        try {
          const response = await this.repository.enviarANomina(
            encabezado.nmconsecutivo
          );
          if (response.exitoso) {
            this.messageService.add({
              severity: 'success',
              summary: 'Enviado',
              detail: response.mensaje
            });
            this.buscar(); // Recargar lista
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.mensaje
            });
          }
        } catch (error) {
          console.error('Error enviando a nómina:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail:
              error instanceof Error
                ? error.message
                : 'No se pudo enviar a nómina'
          });
        } finally {
          this.enviando.set(false);
        }
      }
    });
  }
}
